import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, FileJson, FileSpreadsheet, ChevronRight, Loader2, AlertTriangle, Lock, LockOpen } from "lucide-react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { SeverityBadge, StatusBadge, SEVERITY_META, bucketOf } from "../components/SeverityBadge";
import { CoverageMatrix } from "../components/CoverageMatrix";
import { AgentCta } from "../components/AgentCta";
import { SeverityDonut, CoverageAreaBars } from "../components/Charts";
import { PurchaseDialog } from "../components/PurchaseDialog";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

const CARDS = [
  { key: "critical_count", bucket: "critical", label: "Kritická zjištění" },
  { key: "high_count", bucket: "high", label: "Závažná zjištění" },
  { key: "medium_count", bucket: "medium", label: "Středně závažná zjištění" },
  { key: "unverifiable_count", bucket: "unverifiable", label: "Nebylo možné ověřit" },
];

const TEASER_CARDS = CARDS.map((c) =>
  c.bucket === "unverifiable" ? { ...c, label: "Prověří plný audit" } : c);

const BUCKET_ORDER = { critical: 0, high: 1, medium: 2, unverifiable: 3, ok: 4 };

export default function AuditResults() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState(null);
  const [areaFilter, setAreaFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mandatoryOnly, setMandatoryOnly] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadResults = useCallback(async () => {
    const { data } = await api.get(`/audits/${auditId}/results`);
    setAudit(data.audit);
    setResults(data);
  }, [auditId]);

  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/audits/${auditId}`);
        if (data.status === "queued") {
          try {
            const { data: pr } = await api.get(`/audits/${auditId}/progress`);
            data.progress = pr.progress;
            data.queue_position = pr.queue_position;
          } catch (e) { /* noop */ }
        }
        setAudit(data);
        if (data.status === "done") {
          await loadResults();
        } else if (data.status === "running" || data.status === "queued") {
          timer = setTimeout(poll, 2500);
        }
      } catch (e) {
        toast.error(errMsg(e));
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [auditId, loadResults]);

  const doExport = async (format) => {
    setExporting(true);
    try {
      const res = await api.post(`/audits/${auditId}/export`, { format }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `svp-audit-${auditId}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export se nezdařil");
    } finally {
      setExporting(false);
    }
  };

  if (!audit) {
    return <Layout><div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div></Layout>;
  }

  if (audit.status === "failed" || audit.status === "cancelled") {
    return (
      <Layout>
        <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-red-50 p-8 text-center" data-testid="audit-failed-box">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-600" />
          <h2 className="mt-3 text-lg font-bold text-slate-900">{audit.status === "cancelled" ? "Kontrola byla zrušena" : "Audit se nezdařil"}</h2>
          <p className="mt-1 text-sm text-slate-600">{audit.status === "cancelled" ? "Dokument zůstává nahraný — kontrolu můžete kdykoli spustit znovu z přehledu." : audit.error}</p>
          <Button className="mt-4" variant="outline" onClick={() => navigate("/dashboard")} data-testid="back-home-button">Zpět na přehled</Button>
        </div>
      </Layout>
    );
  }

  if (audit.status === "running" || audit.status === "queued" || !results) {
    const p = audit.progress || {};
    const deep = audit.paid && audit.tier === "full";
    const queued = audit.status === "queued";
    return (
      <Layout>
        <div className="mx-auto max-w-xl py-20 text-center fade-in" data-testid="audit-progress-box">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-emerald-600" />
          <h2 className="mt-5 text-xl font-bold text-slate-900">
            {queued ? "Čekáte ve frontě" : deep ? "Probíhá hloubková analýza (600+ kontrol)" : "Probíhá kontrola vašeho ŠVP"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">{audit.document_filename}</p>
          {queued && (
            <p className="mt-3 text-sm font-semibold text-amber-700" data-testid="queue-position-text">
              Vaše kontrola je {audit.queue_position ? `${audit.queue_position}. v pořadí` : "zařazena do fronty"} — spustí se automaticky, nemusíte nic dělat.
            </p>
          )}
          <div className="mt-6">
            <Progress value={p.percent || 0} className="h-2" />
            <p className="mt-2 text-sm font-medium text-emerald-700" data-testid="audit-progress-stage">
              {p.stage} {p.detail && `· ${p.detail}`}
            </p>
          </div>
          <p className="mt-6 text-xs text-slate-400">
            {deep
              ? "Platba přijata — audit engine nyní prochází celý dokument do hloubky: 600+ kontrol, coverage OVU a verifikace kritických zjištění. Obvykle trvá 3–5 minut."
              : "Audit engine provádí deterministické i AI kontroly. Obvykle trvá 1–3 minuty."}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="outline" className="h-12 px-6 text-base" data-testid="run-in-background-button" onClick={() => navigate("/dashboard")}>
              Nechat běžet na pozadí
            </Button>
            <Button variant="outline" className="h-12 border-red-200 px-6 text-base text-red-700 hover:bg-red-50" data-testid="cancel-audit-button"
              onClick={async () => {
                try {
                  await api.post(`/audits/${auditId}/cancel`);
                  toast.info("Kontrola byla zrušena");
                  navigate("/dashboard");
                } catch (e) { toast.error(errMsg(e)); }
              }}>
              Zrušit kontrolu
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const s = audit.summary || {};

  if (results.locked) {
    return (
      <Layout>
        <TeaserView audit={audit} results={results} onUnlocked={loadResults} />
      </Layout>
    );
  }

  const findings = [...results.findings].sort((a, b) =>
    (BUCKET_ORDER[bucketOf(a)] - BUCKET_ORDER[bucketOf(b)]) || (b.severity - a.severity));
  let shown = filter ? findings.filter((f) => bucketOf(f) === filter) : findings.filter((f) => bucketOf(f) !== "ok");
  if (areaFilter !== "all") shown = shown.filter((f) => f.area === areaFilter);
  if (statusFilter !== "all") shown = shown.filter((f) => f.status === statusFilter);
  if (mandatoryOnly) shown = shown.filter((f) => f.requirement_class === "MANDATORY");
  const okFindings = findings.filter((f) => bucketOf(f) === "ok");
  const allAreas = [...new Set(findings.map((f) => f.area))];
  const coverageAreas = s.coverage_areas || computeAreas(results.coverage);

  return (
    <Layout>
      <div className="stagger">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Výsledek kontroly vašeho ŠVP</h1>
            <p className="mt-1 text-sm text-slate-500">
              {audit.document_filename} · Framework: {audit.framework_pack_id} · Checklist: {audit.checklist_version} · Model: {audit.model_provider}/{audit.model_name}
            </p>
          </div>
          <div className="flex gap-2">
            {audit.demo ? (
              <Button className="h-12 bg-emerald-600 px-6 text-base font-bold hover:bg-emerald-700" data-testid="demo-cta-button"
                onClick={() => navigate("/start")}>
                Vyzkoušet na svém ŠVP
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" disabled={exporting} onClick={() => doExport("pdf")} data-testid="export-pdf-button">
                  <Download className="mr-1.5 h-3.5 w-3.5" /> PDF report
                </Button>
                <Button variant="outline" size="sm" disabled={exporting} onClick={() => doExport("xlsx")} data-testid="export-xlsx-button">
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> Excel
                </Button>
                <Button variant="outline" size="sm" disabled={exporting} onClick={() => doExport("json")} data-testid="export-json-button">
                  <FileJson className="mr-1.5 h-3.5 w-3.5" /> JSON
                </Button>
              </>
            )}
          </div>
        </div>

        {audit.demo && (
          <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 px-5 py-3.5 text-sm text-slate-800" data-testid="demo-banner">
            <b>Ukázkový audit fiktivní mateřské školy.</b> Přesně takhle vypadá plná verze — každé zjištění má vysvětlení
            a důkaz s citací přímo z dokumentu (rozklikněte kterékoli). Vaše skutečné ŠVP zkontrolujeme stejně důkladně.
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {CARDS.map((c) => {
            const m = SEVERITY_META[c.bucket];
            const active = filter === c.bucket;
            return (
              <button key={c.key} data-testid={`severity-card-${c.bucket}`}
                onClick={() => setFilter(active ? null : c.bucket)}
                className={`rounded-xl border bg-white p-5 text-left shadow-sm transition-all hover:shadow-md ${active ? "ring-2 ring-emerald-500" : ""} ${m.border}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} />
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</span>
                </div>
                <div className={`mt-2 text-3xl font-extrabold ${m.text}`}>{s[c.key] ?? 0}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
          <span data-testid="mandatory-summary">Povinné požadavky se zjištěním: <b className="text-slate-800">{s.mandatory_failed}/{s.mandatory_total}</b></span>
          <span>Splněná kritéria: <b className="text-slate-800">{s.ok_count}</b></span>
          <span>Vyžaduje lidské ověření: <b className="text-slate-800">{s.needs_review_count}</b></span>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <SeverityDonut summary={s} />
          <CoverageAreaBars areas={coverageAreas} />
        </div>

        <div className="mt-4 rounded-xl border border-sky-100 bg-sky-50/70 p-4 text-xs leading-relaxed text-slate-600" data-testid="results-legend">
          <b className="text-slate-800">Jak číst výsledky:</b> Barva označuje závažnost zjištění —
          <span className="mx-1 inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-600" />kritické</span>,
          <span className="mx-1 inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-500" />závažné</span>,
          <span className="mx-1 inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" />středně závažné</span>,
          <span className="mx-1 inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-400" />neověřitelné</span>.
          Štítek „Povinné“ znamená závazný požadavek RVP PV. Důležité: „Nenalezeno v ŠVP“ neznamená, že to vaše škola nedělá —
          pouze že to nebylo v dokumentu doloženo.
        </div>

        <Tabs defaultValue="findings" className="mt-8">
          <TabsList>
            <TabsTrigger value="findings" data-testid="tab-findings">Zjištění ({shown.length})</TabsTrigger>
            <TabsTrigger value="coverage" data-testid="tab-coverage">Mapa pokrytí OVU</TabsTrigger>
            <TabsTrigger value="ok" data-testid="tab-ok">Splněno ({okFindings.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="findings" className="mt-4">
            <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="findings-filter-bar">
              <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} data-testid="area-filter-select"
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">
                <option value="all">Všechny oblasti</option>
                {allAreas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter-select"
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">
                <option value="all">Všechny stavy</option>
                <option value="NESPLNENO">Nesplněno</option>
                <option value="CASTECNE">Částečně splněno</option>
                <option value="NEOVERITELNE">Neověřitelné</option>
              </select>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-700">
                <input type="checkbox" checked={mandatoryOnly} onChange={(e) => setMandatoryOnly(e.target.checked)}
                  data-testid="mandatory-only-checkbox" className="accent-emerald-600" />
                Jen povinné požadavky
              </label>
              {(areaFilter !== "all" || statusFilter !== "all" || mandatoryOnly || filter) && (
                <button onClick={() => { setAreaFilter("all"); setStatusFilter("all"); setMandatoryOnly(false); setFilter(null); }}
                  className="text-xs font-medium text-emerald-700 underline" data-testid="clear-filters-button">Zrušit filtry</button>
              )}
            </div>
            <FindingList findings={shown} auditId={auditId} navigate={navigate} />
          </TabsContent>
          <TabsContent value="coverage" className="mt-4">
            <CoverageMatrix coverage={results.coverage} auditId={auditId} />
          </TabsContent>
          <TabsContent value="ok" className="mt-4">
            <FindingList findings={okFindings} auditId={auditId} navigate={navigate} />
          </TabsContent>
        </Tabs>

        <div className="mt-10">
          {!audit.demo && <AgentCta audit={audit} />}
        </div>
      </div>
    </Layout>
  );
}

const FindingList = ({ findings, auditId, navigate }) => {
  if (findings.length === 0) return <p className="py-8 text-center text-sm text-slate-400">Žádná zjištění v této kategorii.</p>;
  const areas = [...new Set(findings.map((f) => f.area))];
  return (
    <div className="space-y-6">
      {areas.map((area) => (
        <div key={area}>
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-500">{area}</h3>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
            {findings.filter((f) => f.area === area).map((f) => (
              <button key={f.id} data-testid={`finding-item-${f.criterion_id}`}
                onClick={() => navigate(`/audits/${auditId}/findings/${f.id}`)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <SeverityBadge finding={f} />
                    <StatusBadge status={f.status} />
                    {f.requirement_class === "MANDATORY" && (
                      <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Povinné</span>
                    )}
                    {f.human_review && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${f.human_review.action === "confirm" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                        {f.human_review.action === "confirm" ? "Potvrzeno" : "Rozporováno"}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 truncate text-sm font-semibold text-slate-900">{f.finding_title}</div>
                  <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">{f.summary}</div>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-400">
                    {f.evidence?.length > 0 && <span>Nalezeno: {f.evidence.map((e) => `str. ${e.page}`).join(", ")}</span>}
                    {f.needs_human_review && <span className="text-amber-600">Doporučujeme lidské ověření</span>}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

function computeAreas(coverage) {
  if (!coverage?.length) return [];
  const map = {};
  coverage.forEach((r) => {
    const st = map[r.area_name] || (map[r.area_name] = { area: r.area_name, total: 0, covered: 0, partial: 0, not_found: 0, unverifiable: 0 });
    st.total += 1;
    if (r.coverage === "EXPLICIT" || r.coverage === "IMPLICIT") st.covered += 1;
    else if (r.coverage === "PARTIAL") st.partial += 1;
    else if (r.coverage === "NOT_FOUND") st.not_found += 1;
    else st.unverifiable += 1;
  });
  return Object.values(map);
}

const TeaserView = ({ audit, results, onUnlocked }) => {
  const s = audit.summary || {};
  return (
    <div className="stagger" data-testid="teaser-view">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Výsledek kontroly vašeho ŠVP</h1>
      <p className="mt-1 text-sm text-slate-500">{audit.document_filename} · bezplatný náhled výsledků</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {TEASER_CARDS.map((c) => {
          const m = SEVERITY_META[c.bucket];
          return (
            <div key={c.key} data-testid={`severity-card-${c.bucket}`}
              className={`rounded-xl border bg-white p-5 shadow-sm ${m.border}`}>
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</span>
              </div>
              <div className={`mt-2 text-3xl font-extrabold ${m.text}`}>{s[c.key] ?? 0}</div>
            </div>
          );
        })}
      </div>

      {audit.tier === "quick" && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-slate-800" data-testid="quick-tier-note">
          <b>Toto je rychlá prohlídka.</b> Našli jsme <b>minimálně {(s.critical_count ?? 0) + (s.high_count ?? 0) + (s.medium_count ?? 0)} zjištění</b>.
          Plný audit prověří <b>600+ kontrolních bodů</b> do hloubky — zkušenost ukazuje, že odhalí další zjištění a přesně určí, kde v dokumentu problém je.
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <SeverityDonut summary={s} unverifiableLabel="Prověří plný audit" />
        <CoverageAreaBars areas={s.coverage_areas} unverifiableLabel="Prověří plný audit" />
      </div>

      <div className="mt-8">
        <h2 className="text-base font-bold text-slate-900">Ukázka nejzávažnějších zjištění</h2>
        <div className="mt-3 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {(results.sample_findings || []).map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`sample-finding-${f.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <SeverityBadge finding={f} />
                  {f.requirement_class === "MANDATORY" && (
                    <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Povinné</span>
                  )}
                  <span className="text-xs text-slate-400">{f.area}</span>
                </div>
                <div className="mt-1.5 truncate text-sm font-semibold text-slate-900">{f.finding_title}</div>
              </div>
              <Lock className="h-4 w-4 shrink-0 text-slate-300" />
            </div>
          ))}
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between gap-4 px-5 py-4 select-none">
              <div className="min-w-0 blur-[5px]">
                <div className="h-4 w-40 rounded bg-slate-200" />
                <div className="mt-2 h-3 w-72 max-w-full rounded bg-slate-100" />
              </div>
              <Lock className="h-4 w-4 shrink-0 text-slate-300" />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 shadow-sm" data-testid="paywall-card">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="text-xl font-extrabold tracking-tight text-slate-900">Odemkněte plný přístup k AuditŠVP</h2>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
              <li>• 5 hloubkových kontrol v ceně — ŠVP můžete po úpravách nechat zkontrolovat znovu</li>
              <li>• {audit.tier === "quick" ? "Kompletní hloubková analýza 600+ kontrolních bodů" : `Všech ${s.total_findings ?? "90+"} zjištění s vysvětlením a umístěním v dokumentu`}</li>
              <li>• Důkazy — vaše ŠVP otevřené přímo na citované straně</li>
              <li>• Kompletní mapa pokrytí OVU a klíčových kompetencí</li>
            </ul>
            <p className="mt-3 text-xs text-slate-500">
              Po vyplnění fakturačních údajů ověříme telefon SMS kódem a vystavíme fakturu.
              Audit se odemkne po připsání platby.
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-extrabold text-slate-900">{(results.price_czk || 1990).toLocaleString("cs-CZ")} Kč</div>
            <div className="mt-1 text-xs text-slate-500">jednorázová platba</div>
            <PurchaseDialog audit={audit} priceCzk={results.price_czk || 1990} onUnlocked={onUnlocked}
              trigger={
                <Button className="mt-4 h-14 bg-emerald-600 px-10 text-lg font-bold hover:bg-emerald-700" data-testid="unlock-audit-button">
                  <LockOpen className="mr-2 h-5 w-5" /> Odemknout plný audit
                </Button>
              } />
            <a href="/ukazka" target="_blank" rel="noreferrer" data-testid="teaser-demo-link"
              className="mt-3 block text-sm font-semibold text-emerald-700 underline hover:text-emerald-900">
              Nejdřív se podívat, jak vypadá plný audit →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
